import { createFileRoute } from '@tanstack/react-router'
import { Badge, Card, CardContent, CardHeader, CardTitle, Separator } from '@repo/ui'
import { Building2, CreditCard, Globe, Hash, ShoppingCart } from 'lucide-react'
import { PageContent } from '@/components/PageContent'
import { PageHeader } from '@/components/PageHeader'
import { InfoField } from '@/components/InfoField'
import { SalesChannel } from '@/api/user/profile'
import type { UserOrgProfile } from '@/api/user/profile'

export const Route = createFileRoute('/_auth/$orgSlug/account/')({
  head: () => ({ meta: [{ title: 'Account - Snowvault' }] }),
  component: RouteComponent
})

function RouteComponent() {
  const { userOrgProfile } = Route.useRouteContext().user

  if (!userOrgProfile) return null

  const features = buildFeatureList(userOrgProfile.enabled_features).filter((f) => f.enabled)

  return (
    <>
      <PageHeader breadcrumbs={[{ label: 'Account' }]} />
      <PageContent>
        <div className="max-w-xl space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Organisation</CardTitle>
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4">
              <InfoField icon={<Building2 className="size-4" />} label="Name" value={userOrgProfile.org.name} />
              <InfoField icon={<Hash className="size-4" />} label="Slug" value={userOrgProfile.org.slug} />
              <InfoField
                icon={<ShoppingCart className="size-4" />}
                label="Billing"
                value={formatBilling(userOrgProfile.org.sales_channel)}
              />
              <InfoField icon={<CreditCard className="size-4" />} label="Payment">
                {userOrgProfile.org.payment_active ? (
                  <Badge variant="outline" className="border-green-600/30 text-green-600">
                    Active
                  </Badge>
                ) : (
                  <Badge variant="outline" className="border-amber-600/30 text-amber-600">
                    Inactive
                  </Badge>
                )}
              </InfoField>
            </CardContent>
          </Card>

          {features.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Enabled Features</CardTitle>
              </CardHeader>
              <Separator />
              <CardContent className="space-y-4">
                {features.map((f) => (
                  <InfoField key={f.label} icon={<Globe className="size-4" />} label={f.label}>
                    <Badge variant="outline" className="border-green-600/30 text-green-600">
                      Enabled
                    </Badge>
                  </InfoField>
                ))}
              </CardContent>
            </Card>
          )}
        </div>
      </PageContent>
    </>
  )
}

function formatBilling(channel: string): string {
  switch (channel) {
    case SalesChannel.AwsMarketplace:
      return 'AWS Marketplace'
    case SalesChannel.Direct:
      return 'Invoice'
    default:
      return channel
  }
}

function buildFeatureList(features: UserOrgProfile['enabled_features']) {
  return [
    { label: 'Search Agent', enabled: features.search_agent },
    { label: 'Email Agent', enabled: features.email_agent },
    { label: 'Script Agent', enabled: features.script_agent },
    { label: 'Story Board Agent', enabled: features.story_board_agent },
    { label: 'Marketing Agent', enabled: features.marketing_agent },
    { label: 'Content Connectors', enabled: features.content_connectors },
    { label: 'Google Drive Connector', enabled: features.g_drive_connector },
    { label: 'SharePoint Connector', enabled: features.sharepoint_connector },
    { label: 'Web Crawler Connector', enabled: features.webcrawler_connector },
    { label: 'Connector Resync', enabled: features.connector_resync },
    { label: 'Global Content', enabled: features.global_content }
  ]
}
